import React from 'react'
import { X } from 'lucide-react'

type Zone = {
  id: string
  zone_moniker: string
  widget_zone_row?: number
  widget_zone_column?: number
  locations_aggregate?: {
    nodes: {
      id: string
      location_moniker: string
    }[]
  }
}

type WorkerAssignment = {
  id: string
  workerId: string
  teamId: string
  taskTypeId: string
  zoneId: string
  locationId?: string
  completed?: boolean
  softDeleted?: boolean
  timestamp: number
  clientId: string | null
}

type Props = {
  zones: Zone[]
  selectedZone: Zone | null
  selectedLocations: Array<{ id: string; location_moniker: string }>
  onZoneSelect: (zone: Zone) => void
  onLocationsSelect: (locations: Array<{ id: string; location_moniker: string }>) => void
  selectedTaskId?: string
  workerAssignments?: WorkerAssignment[]
}

const ZoneLocationPicker: React.FC<Props> = ({
  zones,
  selectedZone,
  selectedLocations,
  onZoneSelect,
  onLocationsSelect,
  selectedTaskId,
  workerAssignments,
}) => {
  const toggleZone = (zone: Zone) => {
    // Always set the zone and clear locations when selecting a zone
    onZoneSelect(zone)
    onLocationsSelect([])
  }

  const toggleLocation = (location: { id: string; location_moniker: string }) => {
    const isSelected = selectedLocations.some(l => l.id === location.id)
    if (isSelected) {
      onLocationsSelect(selectedLocations.filter(l => l.id !== location.id))
    } else {
      onLocationsSelect([...selectedLocations, location])
    }
  }

  const clearLocations = () => {
    onLocationsSelect([])
  }

  // Count unique workers assigned to a zone for a specific task
  const getWorkerCountForZone = (zoneId: string, taskTypeId?: string): number => {
    if (!workerAssignments || !taskTypeId) return 0
    
    const uniqueWorkers = new Set<string>()
    workerAssignments.forEach((assignment) => {
      if (
        assignment.zoneId === zoneId &&
        assignment.taskTypeId === taskTypeId &&
        !assignment.softDeleted
      ) {
        uniqueWorkers.add(assignment.workerId)
      }
    })
    return uniqueWorkers.size
  }

  // Group zones by column (this will be the display row)
  const zonesByColumn: Record<number, Zone[]> = {}
  zones.forEach((zone) => {
    const column = zone.widget_zone_column ?? 0
    if (!zonesByColumn[column]) zonesByColumn[column] = []
    zonesByColumn[column].push(zone)
  })

  // Sort zones in each row by widget_zone_row (numeric sort)
  Object.keys(zonesByColumn).forEach((colKey) => {
    const colNum = parseInt(colKey)
    zonesByColumn[colNum].sort((a, b) => {
      const rowA = a.widget_zone_row ?? 0
      const rowB = b.widget_zone_row ?? 0
      return rowA - rowB
    })
  })

  // Get sorted column numbers
  const sortedColumns = Object.keys(zonesByColumn).map(Number).sort((a, b) => a - b)

  const locations = selectedZone?.locations_aggregate?.nodes || []

  return (
    <div className='flex flex-col gap-2'>
      <label className='text-sm font-semibold text-gray-700'>Zone</label>

      <div className='overflow-x-auto -mx-4 px-4'>
        <div className='flex flex-col gap-3'>
          {sortedColumns.map((column) => (
            <div key={column} className='flex gap-3 flex-nowrap'>
              {zonesByColumn[column].map((zone) => {
                const workerCount = getWorkerCountForZone(zone.id, selectedTaskId)
                return (
                  <button
                    key={zone.id}
                    onClick={() => toggleZone(zone)}
                    className={`w-14 h-14 flex items-center justify-center text-xs font-medium rounded-lg transition-colors flex-shrink-0 relative ${
                      selectedZone?.id === zone.id
                        ? 'bg-blue-700 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {zone.zone_moniker}
                    {workerCount > 0 && (
                      <div className='absolute top-1 right-1 flex gap-0.5 flex-wrap justify-end max-w-[30px]'>
                        {Array.from({ length: workerCount }).map((_, i) => (
                          <div
                            key={i}
                            className='w-1.5 h-1.5 bg-green-500 rounded-full'
                            title={`${workerCount} worker${workerCount !== 1 ? 's' : ''} assigned`}
                          />
                        ))}
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      </div>

      {selectedZone && locations.length > 0 && (
        <div className='pt-2'>
          <div className='flex items-center justify-between mb-3'>
            <label className='text-sm font-semibold text-gray-700'>Locations (Optional)</label>
            {selectedLocations.length > 0 && (
              <button
                onClick={clearLocations}
                className='text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1'
              >
                <X size={14} /> Clear All
              </button>
            )}
          </div>

          <div className='overflow-x-auto -mx-4 px-4'>
            <div className='flex gap-3 flex-nowrap'>
              {locations.map((location) => {
                const isSelected = selectedLocations.some(l => l.id === location.id)
                return (
                  <button
                    key={location.id}
                    onClick={() => toggleLocation(location)}
                    className={`w-14 h-14 flex items-center justify-center text-xs font-medium rounded-lg transition-colors flex-shrink-0 text-center ${
                      isSelected
                        ? 'bg-green-700 text-white border-2 border-green-700'
                        : 'bg-gray-50 border-2 border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {location.location_moniker}
                  </button>
                )
              })}
            </div>
          </div>

          <p className='text-xs text-gray-500 mt-2'>
            {selectedLocations.length > 0 
              ? `Selected: ${selectedLocations.map(l => l.location_moniker).join(', ')}` 
              : ''}
          </p>
        </div>
      )}
    </div>
  )
}

export default ZoneLocationPicker
